import React, { useState, useEffect } from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Checkbox from '@material-ui/core/Checkbox';
import { withStyles } from '@material-ui/core/styles';
import { BoothTaxonomy } from 'models/cms/boothTaxonomies';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import FilterCheckboxOption from './FilterCheckboxOption';

const RootContainer = styled.div`
  width: 100%;
  padding: 12px 0;
`;

const FilterTitleContainer = styled.div<{ singleLevel?: boolean; selected?: boolean; color: string }>`
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ singleLevel }) => (singleLevel ? 'flex-start' : 'space-between')};
  transition: all 0.3s;
  color:
    ${({ selected, singleLevel, color }) => {
    const selectedColor = color;
    const notSelectedColor = singleLevel ? '#484848' : '#A2AAB3';
    return selected ? selectedColor : notSelectedColor;
  }};
`;

const FilterTitleText = styled.span`
  font-weight: bold;
  font-size: 16px;
  line-height: 18px;
  padding: 12px 0;
  display: inline-block;
  text-transform: none;
`;

const StyledCheckbox = ({ children, ...props }: any) => {
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const Custom = withStyles(() => ({
    colorPrimary: {
      color: themeColor || DEFAULT_THEME_COLOR,
    },
    root: {
      '&$checked': {
        color: themeColor || DEFAULT_THEME_COLOR,
      },
    },
    checked: {},
  }))(Checkbox);
  return <Custom {...props as any}>{children as any}</Custom>;
};

export interface FilterCategoryContainerProps {
  filter: BoothTaxonomy;
  selectedFilter: string[];
  setSelectedFilter: (updater: string[] | ((filter: string[]) => string[])) => null;
  showCollapse: boolean;
}

const FilterCategoryContainer: React.FC<FilterCategoryContainerProps> = ({
  filter,
  selectedFilter,
  setSelectedFilter,
  showCollapse,
}: FilterCategoryContainerProps) => {
  const locale = useSelector(selectLocale);
  const { features } = useSelector((state: any) => state.eventConfigs);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (features.includes('multipleHall')) {
      setOpen(showCollapse);
    }
  }, [showCollapse]);

  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const isSingleLevel = !Array.isArray(filter.subcategories);
  const isSelected = !isSingleLevel || !!selectedFilter.find((it) => it === filter.id);

  const onFilterTitleContainerClick = () => {
    if (isSingleLevel) {
      setSelectedFilter((prev) => {
        const index = prev.findIndex((it) => it === filter.id);
        if (index === -1) return [...Array.from(new Set([...prev, filter.id]))];
        return [...prev.slice(0, index), ...prev.slice(index + 1)];
      });
    }
    setOpen((prev) => !prev);
  };

  return (
    <RootContainer>
      <FilterTitleContainer
        singleLevel={isSingleLevel}
        selected={isSelected}
        onClick={onFilterTitleContainerClick}
        color={themeColor || DEFAULT_THEME_COLOR}
      >
        {isSingleLevel && (
          <StyledCheckbox
            checked={isSelected}
            color="primary"
          />
        )}
        <FilterTitleText>
          {filter.name[locale] || Object.values(filter.name).filter((it) => it)[0]}
        </FilterTitleText>
        {!isSingleLevel && (
          open
            ? <RemoveIcon style={{ paddingLeft: '4px' }} />
            : <AddIcon style={{ paddingLeft: '4px' }} />
        )}
      </FilterTitleContainer>
      {!isSingleLevel && (
        <Collapse isOpened={open}>
          {filter.subcategories.map((sub) => (
            <FilterCheckboxOption
              key={sub.id}
              filter={sub}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
            />
          ))}
        </Collapse>
      )}
    </RootContainer>
  );
};

export default FilterCategoryContainer;
